import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';

const PLATFORM_COLORS: Record<string, string> = {
  placefull: '#3b82f6',
  qubica: '#8b5cf6',
  partywirks: '#22c55e',
  'roller-haveablast': '#f59e0b',
  roller: '#ef4444',
  rex: '#06b6d4',
  alleytrak: '#ec4899',
  funbutler: '#84cc16',
};
const colorFor = (id: string) => PLATFORM_COLORS[id] || '#9ca3af';

const SERIES_WINDOWS = ['15m', '1h', '6h', '24h'];

const AUTO_REFRESH_MS = 15 * 1000;

function StatCard({ label, value, unit, color }: { label: string; value: string | number; unit?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid var(--gray-200)',
      borderRadius: '8px',
      padding: '16px 20px',
      flex: '1 1 0',
      minWidth: '130px',
    }}>
      <div style={{ fontSize: '11px', color: 'var(--gray-500)', fontWeight: 500, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '26px', fontWeight: 700, color: color || 'var(--gray-900)', lineHeight: 1.1 }}>
        {value}{unit && <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--gray-400)', marginLeft: '2px' }}>{unit}</span>}
      </div>
    </div>
  );
}

type StateItem = { label: string; value: string | number; color: string; live?: boolean; note?: string };

function StatePill({ label, value, color, live, note }: StateItem) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid var(--gray-200)',
      borderLeft: `4px solid ${color}`,
      borderRadius: '8px',
      padding: '12px 16px',
      flex: '1 1 0',
      minWidth: '120px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        {live && <span title="live count" style={{ width: '7px', height: '7px', borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />}
        <span style={{ fontSize: '11px', color: 'var(--gray-500)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </span>
        {note && <span style={{ fontSize: '10px', color: 'var(--gray-400)', fontWeight: 500 }}>{note}</span>}
      </div>
      <div style={{ fontSize: '24px', fontWeight: 700, color, lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}

function StateGroup({ title, subtitle, items }: { title: string; subtitle: string; items: StateItem[] }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ marginBottom: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-800)' }}>{title}</span>
        <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginLeft: '8px' }}>{subtitle}</span>
      </div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {items.map((it) => <StatePill key={it.label} {...it} />)}
      </div>
    </div>
  );
}

const N = (v: any) => Number(v ?? 0);

function ThroughputSection({ throughput, pendingByType }: { throughput: any[]; pendingByType: any[] }) {
  const rows = throughput || [];
  const pendingMap: Record<string, number> = {};
  (pendingByType || []).forEach((p) => { pendingMap[p.platform_id] = N(p.waiting); });
  // Include platforms that have a waiting backlog even if 0 recent throughput.
  const allIds: string[] = [];
  rows.forEach((r) => { if (!allIds.includes(r.platform_id)) allIds.push(r.platform_id); });
  Object.keys(pendingMap).forEach((id) => { if (!allIds.includes(id)) allIds.push(id); });
  const merged = allIds.map((id) => {
    const r = rows.find((x) => x.platform_id === id);
    return {
      platform_id: id,
      per_sec: N(r?.per_sec),
      per_min: N(r?.per_min),
      done_60s: N(r?.done_60s),
      failed_5m: N(r?.failed_5m),
      waiting: pendingMap[id] || 0,
    };
  }).sort((a, b) => b.per_min - a.per_min || b.waiting - a.waiting);

  const totalPerSec = merged.reduce((s, r) => s + r.per_sec, 0);
  const totalPerMin = merged.reduce((s, r) => s + r.per_min, 0);
  const totalWaiting = merged.reduce((s, r) => s + r.waiting, 0);
  const totalFailed = merged.reduce((s, r) => s + r.failed_5m, 0);

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ marginBottom: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-800)' }}>Live Throughput</span>
        <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginLeft: '8px' }}>completed syncs · rolling 5-min rate · by sync type</span>
      </div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <StatePill label="Throughput" value={totalPerSec.toFixed(2)} color="#22c55e" live note="/sec" />
        <StatePill label="Throughput" value={totalPerMin.toFixed(0)} color="#22c55e" live note="/min" />
        <StatePill label="Requests Waiting To Send" value={totalWaiting.toLocaleString()} color={totalWaiting > 1000 ? '#f59e0b' : '#9ca3af'} live />
        <StatePill label="Failed" value={totalFailed} color={totalFailed > 0 ? '#ef4444' : '#9ca3af'} note="5m" />
      </div>
      <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px' }}>Sync Type</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Per Sec</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Per Min</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Last 60s</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Waiting To Send</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Failed (5m)</th>
            </tr>
          </thead>
          <tbody>
            {merged.map((r) => (
              <tr key={r.platform_id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>{r.platform_id}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.per_sec.toFixed(2)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.per_min.toFixed(1)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--gray-500)' }}>{r.done_60s}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.waiting > 1000 ? '#f59e0b' : 'var(--gray-700)' }}>{r.waiting.toLocaleString()}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.failed_5m > 0 ? '#ef4444' : 'var(--gray-400)' }}>{r.failed_5m}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrendSection() {
  const [window, setWindow] = useState('1h');
  const [view, setView] = useState<'graph' | 'table'>('graph');
  const [points, setPoints] = useState<any[]>([]);
  const [bucket, setBucket] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.getThroughputSeries(window);
        if (!cancelled) { setPoints(res.points || []); setBucket(res.bucket || ''); }
      } catch { /* leave previous data */ }
    };
    load();
    const t = setInterval(load, 30 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [window]);

  // Pivot rows -> one object per time bucket with a column per platform.
  const platforms: string[] = [];
  const byBucket: Record<string, any> = {};
  points.forEach((p) => {
    if (!platforms.includes(p.platform_id)) platforms.push(p.platform_id);
    const k = p.bucket;
    if (!byBucket[k]) byBucket[k] = { bucket: k, total: 0 };
    byBucket[k][p.platform_id] = N(p.completed);
    byBucket[k].total += N(p.completed);
  });
  const data = Object.values(byBucket)
    .sort((a: any, b: any) => new Date(a.bucket).getTime() - new Date(b.bucket).getTime())
    .map((row: any) => ({ ...row, label: new Date(row.bucket).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }));
  platforms.sort();

  const btn = (active: boolean) => ({
    padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    border: '1px solid var(--gray-200)', borderRadius: '6px',
    background: active ? 'var(--gray-900)' : '#fff', color: active ? '#fff' : 'var(--gray-600)',
  });

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
        <div>
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-800)' }}>Throughput Trend</span>
          <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginLeft: '8px' }}>completed syncs per {bucket || 'bucket'}, by sync type</span>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {SERIES_WINDOWS.map((w) => (
              <button key={w} style={btn(window === w)} onClick={() => setWindow(w)}>{w}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button style={btn(view === 'graph')} onClick={() => setView('graph')}>Graph</button>
            <button style={btn(view === 'table')} onClick={() => setView('table')}>Table</button>
          </div>
        </div>
      </div>

      {data.length === 0 ? (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--gray-400)', fontSize: '13px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
          No completed syncs in this window.
        </div>
      ) : view === 'graph' ? (
        <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '12px 8px 4px' }}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--gray-500)' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: 'var(--gray-500)' }} allowDecimals={false} width={36} />
              <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px' }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              {platforms.map((pid) => (
                <Bar key={pid} dataKey={pid} stackId="a" fill={colorFor(pid)} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px' }}>Time</th>
                {platforms.map((pid) => <th key={pid} style={{ padding: '8px 12px', textAlign: 'right' }}>{pid}</th>)}
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {data.slice().reverse().map((row: any) => (
                <tr key={row.bucket} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '8px 12px', fontVariantNumeric: 'tabular-nums' }}>{row.label}</td>
                  {platforms.map((pid) => (
                    <td key={pid} style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: row[pid] ? 'var(--gray-700)' : 'var(--gray-300)' }}>{row[pid] || 0}</td>
                  ))}
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatFreshness(secs: number | null): string {
  if (secs == null) return '-';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
}

function freshnessColor(secs: number, intervalMin: number): string {
  const thresholdSecs = intervalMin * 60;
  if (secs <= thresholdSecs) return 'var(--gray-900)';
  if (secs <= thresholdSecs * 1.5) return '#f59e0b';
  return '#ef4444';
}

function cadenceColor(withinInterval: boolean | string | null | undefined): string {
  if (withinInterval === true || withinInterval === 'true') return '#16a34a';
  if (withinInterval === false || withinInterval === 'false') return '#dc2626';
  return 'var(--gray-500)';
}

function CadenceHealthSection({ rows, window }: { rows: any[]; window?: string }) {
  if (!rows?.length) {
    return (
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 6px' }}>Cadence Health</h3>
        <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: '13px' }}>No terminal syncs in the measurement window.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: '24px' }}>
      <h3 style={{ margin: '0 0 4px' }}>Cadence Health</h3>
      <p style={{ margin: '0 0 12px', color: 'var(--gray-500)', fontSize: '13px' }}>
        p99 enqueue-to-terminal time versus each target’s current configured interval · rolling {window || '24 hours'} · terminal jobs only
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px' }}>Platform</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Requested interval</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Terminal samples</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>p95 terminal</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>p99 terminal</th>
              <th style={{ padding: '8px 12px' }}>p99 status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any) => {
              const withinInterval = row.p99_within_interval === true || row.p99_within_interval === 'true';
              return (
                <tr key={`${row.platform_id}-${row.sync_interval_minutes}`} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{row.platform_id}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.sync_interval_minutes}m</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{N(row.completed_24h).toLocaleString()}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatFreshness(N(row.due_to_terminal_p95_secs))}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatFreshness(N(row.due_to_terminal_p99_secs))}</td>
                  <td style={{ padding: '8px 12px', color: cadenceColor(row.p99_within_interval), fontWeight: 600 }}>
                    {withinInterval ? 'Within interval' : 'Exceeds interval'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SystemHealthScreen() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      setError(null);
      const result = await api.getSystemHealth();
      setData(result);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, AUTO_REFRESH_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchHealth]);

  if (loading && !data) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--gray-900)' }}>System Health</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: '14px' }}>Sync pipeline status and freshness monitoring</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastRefresh && <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
          <button className="btn btn-primary btn-sm" onClick={() => { setLoading(true); fetchHealth(); }} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {data && (
        <>
          {/* Live throughput by sync type — the primary operational signal */}
          {(data.throughput || data.pendingByType) && (
            <ThroughputSection throughput={data.throughput} pendingByType={data.pendingByType} />
          )}

          {/* Mixed-cadence fleet freshness is descriptive; interval compliance is below. */}
          {data.freshness && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-800)' }}>Fleet Freshness</span>
                <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginLeft: '8px' }}>descriptive only across mixed configured intervals</span>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <StatCard label="p50" value={formatFreshness(N(data.freshness.p50_secs))} />
                <StatCard label="p95" value={formatFreshness(N(data.freshness.p95_secs))} />
                <StatCard label="p99" value={formatFreshness(N(data.freshness.p99_secs))} />
                <StatCard label="p99.9" value={formatFreshness(N(data.freshness.p999_secs))} />
              </div>
            </div>
          )}

          <CadenceHealthSection rows={data.cadenceByPlatformInterval || []} window={data.cadenceWindow} />

          <TrendSection />

          {/* Pipeline State — every state enumerated across the three granularities */}
          <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--gray-500)' }}>
            The pipeline fans out across three levels: each <strong>venue</strong> runs a <strong>sync job</strong>, which issues many <strong>outbound requests</strong>.
            The dot (<span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />) marks live in-flight counts; <em>1h</em> marks a rolling one-hour window.
          </div>

          <StateGroup
            title="Venues (targets)"
            subtitle={`${N(data.summary.total_enabled)} enabled · ${N(data.summary.total_disabled)} disabled`}
            items={[
              { label: 'Succeeded', value: data.summary.succeeded, color: '#22c55e' },
              { label: 'Syncing', value: data.summary.syncing, color: '#3b82f6', live: true },
              { label: 'Queued', value: data.summary.queued, color: '#f59e0b', live: true },
              { label: 'Idle', value: data.summary.idle, color: '#9ca3af' },
              { label: 'Failed', value: data.summary.failed, color: N(data.summary.failed) > 0 ? '#ef4444' : '#9ca3af' },
              { label: 'Never Synced', value: data.summary.never_synced, color: N(data.summary.never_synced) > 0 ? '#f97316' : '#9ca3af' },
              { label: 'Disabled', value: data.summary.total_disabled, color: '#6b7280' },
            ]}
          />

          {data.jobStates && (
            <StateGroup
              title="Sync jobs"
              subtitle="one per venue sync run"
              items={[
                { label: 'Queued', value: data.jobStates.queued, color: '#f59e0b', live: true },
                { label: 'Processing', value: data.jobStates.processing, color: '#3b82f6', live: true },
                { label: 'Finalizing', value: data.jobStates.finalizing, color: '#8b5cf6', live: true },
                { label: 'Succeeded', value: data.jobStates.succeeded_1h, color: '#22c55e', note: '1h' },
                { label: 'Failed', value: data.jobStates.failed_1h, color: N(data.jobStates.failed_1h) > 0 ? '#ef4444' : '#9ca3af', note: '1h' },
              ]}
            />
          )}

          {data.requestStates && (
            <StateGroup
              title="Outbound requests"
              subtitle="HTTP calls fanned out from jobs · live backlog"
              items={[
                { label: 'Pending', value: data.requestStates.pending, color: '#f59e0b', live: true },
                { label: 'Processing', value: data.requestStates.processing, color: '#3b82f6', live: true },
              ]}
            />
          )}

          {data.purgeHealth && (() => {
            const purge = data.purgeHealth;
            const backfillHigh = N(purge.high_watermark_sync_job_id);
            const backfillLast = N(purge.last_sync_job_id);
            const backfillInitial = N(purge.initial_sync_job_id);
            const backfillSpan = Math.max(0, backfillHigh - backfillInitial);
            const backfillPct = backfillSpan > 0
              ? Math.min(100, (Math.max(0, backfillLast - backfillInitial) / backfillSpan) * 100).toFixed(1)
              : '100.0';
            const oldestAgeSecs = purge.oldest_source_completed_at
              ? Math.max(0, Math.round((Date.now() - new Date(purge.oldest_source_completed_at).getTime()) / 1000))
              : 0;
            return (
              <StateGroup
                title="PostgreSQL cleanup"
                subtitle={`verified S3 archives → source-row purge · historical adoption ${backfillPct}%`}
                items={[
                  { label: 'Pending purge', value: N(purge.pending).toLocaleString(), color: N(purge.pending) > 0 ? '#f59e0b' : '#22c55e', live: true },
                  { label: 'Purging', value: N(purge.purging).toLocaleString(), color: '#3b82f6', live: true },
                  { label: 'Failed purge', value: N(purge.failed).toLocaleString(), color: N(purge.failed) > 0 ? '#ef4444' : '#9ca3af' },
                  { label: 'Blocked purge', value: N(purge.blocked).toLocaleString(), color: N(purge.blocked) > 0 ? '#ef4444' : '#9ca3af' },
                  { label: 'Legacy rearchive', value: N(purge.requeued_count).toLocaleString(), color: '#6b7280', note: 'cumulative' },
                  { label: 'Skipped review', value: N(purge.skipped_count).toLocaleString(), color: N(purge.skipped_count) > 0 ? '#f59e0b' : '#22c55e' },
                  { label: 'Rows deleted', value: N(purge.rows_deleted_1h).toLocaleString(), color: '#22c55e', note: '1h' },
                  { label: 'Oldest purge debt', value: oldestAgeSecs > 0 ? formatFreshness(oldestAgeSecs) : '-', color: oldestAgeSecs > 8 * 3600 ? '#ef4444' : '#6b7280' },
                ]}
              />
            );
          })()}

          {/* Sync Config Groups */}
          {data.bySyncInterval && data.bySyncInterval.length > 0 && (
            <>
              <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-800)', margin: '24px 0 12px' }}>Sync Config Groups</h2>
              {data.bySyncInterval.map((g: any) => (
                <div key={g.sync_interval_minutes} style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {g.sync_interval_minutes}-min sync ({g.target_count} targets)
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <StatCard label="Succeeded" value={g.succeeded} color="#22c55e" />
                    <StatCard label="Failed" value={g.failed} color={Number(g.failed) > 0 ? '#ef4444' : '#22c55e'} />
                    <StatCard label="Avg Freshness" value={formatFreshness(g.avg_freshness_secs)} />
                    <StatCard label="P95 Freshness" value={formatFreshness(g.p95_freshness_secs)}
                      color={g.p95_freshness_secs > (g.sync_interval_minutes * 60 * 1.5) ? '#ef4444' : 'var(--gray-900)'} />
                  </div>
                </div>
              ))}
            </>
          )}

          {/* By Tier */}
          <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-800)', margin: '24px 0 12px' }}>By Tier</h2>
          {data.byTier.map((t: any) => (
            <div key={t.tier} style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t.tier} ({t.target_count} targets)
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <StatCard label="Succeeded" value={t.succeeded} color="#22c55e" />
                <StatCard label="Failed" value={t.failed} color={Number(t.failed) > 0 ? '#ef4444' : '#22c55e'} />
                <StatCard label="Avg Freshness" value={formatFreshness(t.avg_freshness_secs)} />
                <StatCard label="P95 Freshness" value={formatFreshness(t.p95_freshness_secs)}
                  color={t.p95_freshness_secs > (t.tier === 'T1' ? 2700 : 3600) ? '#ef4444' : 'var(--gray-900)'} />
              </div>
            </div>
          ))}

          {/* Platform Breakdown */}
          <div className="card">
            <h3 style={{ margin: '0 0 12px' }}>Platform Breakdown</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Platform</th>
                    <th style={{ padding: '8px 12px' }}>Tier</th>
                    <th style={{ padding: '8px 12px' }}>Sync</th>
                    <th style={{ padding: '8px 12px' }}>Targets</th>
                    <th style={{ padding: '8px 12px' }}>Succeeded</th>
                    <th style={{ padding: '8px 12px' }}>Failed</th>
                    <th style={{ padding: '8px 12px' }}>Avg Freshness</th>
                    <th style={{ padding: '8px 12px' }}>Stalest</th>
                    <th style={{ padding: '8px 12px' }}>Avg Runtime</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byPlatform.map((p: any) => (
                    <tr key={p.platform_id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{p.display_name}</td>
                      <td style={{ padding: '8px 12px' }}><StatusBadge status={p.tier} /></td>
                      <td style={{ padding: '8px 12px', fontVariantNumeric: 'tabular-nums', color: 'var(--gray-600)' }}>
                        {p.target_count > 0 ? `${p.sync_interval_minutes}m` : '-'}
                      </td>
                      <td style={{ padding: '8px 12px' }}>{p.target_count}</td>
                      <td style={{ padding: '8px 12px', color: '#22c55e' }}>{p.succeeded}</td>
                      <td style={{ padding: '8px 12px', color: Number(p.failed) > 0 ? '#ef4444' : 'var(--gray-500)' }}>{p.failed}</td>
                      <td style={{ padding: '8px 12px', fontVariantNumeric: 'tabular-nums' }}>{formatFreshness(p.avg_freshness_secs)}</td>
                      <td style={{ padding: '8px 12px', fontVariantNumeric: 'tabular-nums', color: freshnessColor(p.max_freshness_secs, p.sync_interval_minutes || 30) }}>
                        {formatFreshness(p.max_freshness_secs)}
                      </td>
                      <td style={{ padding: '8px 12px', fontVariantNumeric: 'tabular-nums', color: 'var(--gray-500)' }}>
                        {p.avg_duration_ms ? `${(p.avg_duration_ms / 1000).toFixed(1)}s` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Failures */}
          {data.failures.length > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #ef4444' }}>
              <h3 style={{ margin: '0 0 12px', color: '#ef4444' }}>Failed Targets ({data.failures.length})</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                      <th style={{ padding: '8px 12px' }}>Platform</th>
                      <th style={{ padding: '8px 12px' }}>Target</th>
                      <th style={{ padding: '8px 12px' }}>Last Sync</th>
                      <th style={{ padding: '8px 12px' }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.failures.map((f: any) => (
                      <tr key={f.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                        <td style={{ padding: '8px 12px' }}>{f.platform_id}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{f.display_name || f.slug}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--gray-500)', fontSize: '12px' }}>
                          {f.last_sync_at ? new Date(f.last_sync_at).toLocaleString() : 'Never'}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#ef4444', fontFamily: 'monospace', fontSize: '11px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.last_error || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--gray-400)' }}>
            Auto-refreshes every 15 seconds
          </div>
        </>
      )}
    </>
  );
}
